"use client";

import Link from "next/link";
import { useState } from "react";
import { hrefRequiresNativeAnchor } from "@/lib/hrefRequiresNativeAnchor";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import Button from "../ui/Button";

type MenuItem = {
  id: string;
  label: string;
  type: "collection" | "link" | "section" | "group";
  sortOrder?: number;
  url?: string;
  collectionId?: string;
  collection?: {
    id: string;
    title: string;
    handle: string;
    bannerImageUrl?: string;
    description?: string;
  };
  children?: MenuItem[];
  badge?: string;
  badgeColor?: string;
  isFeatured?: boolean;
  description?: string;
};

interface NavigationMenuProps {
  items: MenuItem[];
}

export default function NavigationMenu({ items }: NavigationMenuProps) {
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  if (items.length === 0) {
    return null;
  }

  const sortedItems = items
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return (
    <>
      {sortedItems.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const isHovered = hoveredMenuItem === item.id;
        const url =
          item.type === "collection" && item.collection
            ? `/collections/${item.collection.handle}`
            : item.url || "#";

        const isDirectLink =
          !hasChildren &&
          (item.type === "link" || item.type === "collection") &&
          url &&
          url !== "#";

        if (isDirectLink) {
          const linkClass =
            "flex flex-row items-center gap-1 text-base font-medium text-blue-100 transition-colors";
          return (
            <div key={item.id} className="relative flex h-full items-center">
              {hrefRequiresNativeAnchor(url) ? (
                <a href={url} className={linkClass}>
                  {item.type === "collection" && item.collection
                    ? item.collection.title
                    : item.label}
                  {item.badge && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        item.badgeColor === "red"
                          ? "bg-red-5 text-red-50"
                          : item.badgeColor === "green"
                          ? "bg-green-5 text-green-50"
                          : item.badgeColor === "blue"
                          ? "bg-blue-5 text-blue-80"
                          : "bg-neutral-5 text-neutral-60"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </a>
              ) : (
                <Link href={url} className={linkClass}>
                  {item.type === "collection" && item.collection
                    ? item.collection.title
                    : item.label}
                  {item.badge && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        item.badgeColor === "red"
                          ? "bg-red-5 text-red-50"
                          : item.badgeColor === "green"
                          ? "bg-green-5 text-green-50"
                          : item.badgeColor === "blue"
                          ? "bg-blue-5 text-blue-80"
                          : "bg-neutral-5 text-neutral-60"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              )}
            </div>
          );
        }

        return (
          <div
            key={item.id}
            className="relative h-full flex items-center"
            onMouseEnter={() => {
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
              }
              setHoveredMenuItem(item.id);
            }}
            onMouseLeave={() => {
              const timeout = setTimeout(() => {
                setHoveredMenuItem(null);
              }, 150);
              setHoverTimeout(timeout);
            }}
          >
            {/* TODO(button-migration): nav item with optional badge + conditional chevron, complex JSX children. */}
            <Button raw
              type="button"
              className={`flex flex-row items-center gap-1 text-base font-medium transition-colors ${
                isHovered ? "text-blue-80" : "text-blue-100"
              }`}
            >
              {item.type === "collection" && item.collection
                ? item.collection.title
                : item.label}
              {item.badge && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    item.badgeColor === "red"
                      ? "bg-red-5 text-red-50"
                      : item.badgeColor === "green"
                      ? "bg-green-5 text-green-50"
                      : item.badgeColor === "blue"
                      ? "bg-blue-5 text-blue-80"
                      : "bg-neutral-5 text-neutral-60"
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {hasChildren && (
                isHovered ? (
                  <ChevronUpIcon size={12} className="w-3 h-3 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon size={12} className="w-3 h-3 flex-shrink-0" />
                )
              )}
            </Button>
            {hasChildren && isHovered && (
              <div className="absolute top-full left-0 -mt-1 bg-constant-white border border-neutral-10 rounded-xl shadow-lg z-50 px-4 py-2.5">
                <div className="space-y-0 w-max">
                  {item.description && (
                    <div className="pt-1 pb-3">
                      <div className="text-base text-text-secondary">
                        {item.description}
                      </div>
                      <div className="mt-3 h-px bg-neutral-10" />
                    </div>
                  )}
                  {item
                    .children!.slice()
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map((child) => {
                    const childUrl =
                      child.type === "collection" && child.collection
                        ? `/collections/${child.collection.handle}`
                        : child.url || "#";
                    const displayLabel = child.type === "collection" && child.collection
                      ? child.collection.title
                      : child.label;
                    const childClass =
                      "block px-2 py-2 text-base transition-colors rounded-lg text-blue-100 border border-transparent hover:bg-blue-5 hover:border-blue-10";
                    return hrefRequiresNativeAnchor(childUrl) ? (
                      <a key={child.id} href={childUrl} className={childClass}>
                        <div className="flex items-center justify-between gap-2">
                          <span>{displayLabel}</span>
                          {child.badge && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                child.badgeColor === "red"
                                  ? "bg-red-5 text-red-50"
                                  : child.badgeColor === "green"
                                  ? "bg-green-5 text-green-50"
                                  : child.badgeColor === "blue"
                                  ? "bg-blue-5 text-blue-80"
                                  : "bg-neutral-5 text-neutral-60"
                              }`}
                            >
                              {child.badge}
                            </span>
                          )}
                        </div>
                        {child.description && (
                          <p className="text-xs text-neutral-60 mt-0.5">{child.description}</p>
                        )}
                      </a>
                    ) : (
                      <Link key={child.id} href={childUrl} className={childClass}>
                        <div className="flex items-center justify-between gap-2">
                          <span>{displayLabel}</span>
                          {child.badge && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                child.badgeColor === "red"
                                  ? "bg-red-5 text-red-50"
                                  : child.badgeColor === "green"
                                  ? "bg-green-5 text-green-50"
                                  : child.badgeColor === "blue"
                                  ? "bg-blue-5 text-blue-80"
                                  : "bg-neutral-5 text-neutral-60"
                              }`}
                            >
                              {child.badge}
                            </span>
                          )}
                        </div>
                        {child.description && (
                          <p className="text-xs text-neutral-60 mt-0.5">{child.description}</p>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

